<template>
  <div class="min-h-screen bg-white">

    <!-- ========== NAVIGATION ========== -->
    <nav class="sticky top-0 z-50 bg-white border-b border-neutral-100">
      <div class="container">
        <div class="flex items-center justify-between h-16">
          <!-- Logo -->
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 bg-primary-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
              </svg>
            </div>
            <span class="font-display font-bold text-xl text-neutral-900 tracking-tight">Tunga</span>
          </div>

          <!-- Center Nav (desktop) -->
          <div class="hidden md:flex items-center gap-8">
            <a href="#how-it-works" class="text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors no-underline">How It Works</a>
            <a href="#features" class="text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors no-underline">Features</a>
            <a href="#for-you" class="text-sm font-medium text-neutral-500 hover:text-neutral-900 transition-colors no-underline">Get Started</a>
          </div>

          <!-- Auth -->
          <div>
            <a v-if="!authStore.isAuthenticated" :href="loginUrlCreator" class="btn btn-accent no-underline">
              Sign In
            </a>
            <div v-else class="flex items-center gap-3">
              <span class="text-sm text-neutral-600 hidden sm:block">{{ authStore.user?.name }}</span>
              <router-link :to="authStore.user?.is_creator ? '/creator' : '/guardian'" class="btn btn-primary">
                Dashboard
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- ========== HERO ========== -->
    <section class="relative bg-white overflow-hidden">
      <!-- Decorative blobs — solid, no gradient -->
      <div class="hero-blob-teal absolute top-0 right-0 w-[480px] h-[480px] bg-primary-50 rounded-full pointer-events-none" style="transform: translate(30%, -30%)"></div>
      <div class="hero-blob-orange absolute bottom-0 left-0 w-[380px] h-[380px] bg-accent-50 rounded-full pointer-events-none" style="transform: translate(-35%, 40%)"></div>
      <!-- Floating accent dots -->
      <div class="absolute top-20 left-[36%] w-5 h-5 bg-accent-400 rounded-full pointer-events-none"></div>
      <div class="absolute top-48 right-[28%] w-3 h-3 bg-primary-400 rotate-45 pointer-events-none"></div>
      <div class="absolute bottom-28 left-[22%] w-3.5 h-3.5 bg-primary-200 rounded-full pointer-events-none"></div>
      <div class="absolute top-[55%] left-12 w-2 h-2 bg-accent-500 rounded-full pointer-events-none"></div>

      <div class="container relative z-10 py-20 lg:py-28">
        <div class="grid lg:grid-cols-[1fr_420px] gap-16 items-center">

          <!-- Left: Text -->
          <div>
            <!-- Trust signals (icon-based, no label badges) -->
            <div class="flex flex-wrap items-center gap-5 mb-10">
              <div class="flex items-center gap-2 text-sm font-medium text-neutral-500">
                <div class="w-7 h-7 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                  </svg>
                </div>
                CBC Verified
              </div>
              <div class="flex items-center gap-2 text-sm font-medium text-neutral-500">
                <div class="w-7 h-7 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-accent-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                  </svg>
                </div>
                AI-Powered
              </div>
              <div class="flex items-center gap-2 text-sm font-medium text-neutral-500">
                <div class="w-7 h-7 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </div>
                Peer Reviewed
              </div>
            </div>

            <h1 class="font-display font-bold text-neutral-900 mb-6 tracking-tight hero-headline">
              <span class="block leading-[1.05]">Kenya's kids</span>
              <span class="block leading-[1.05] text-primary-600">deserve better</span>
              <span class="block leading-[1.05]">learning.</span>
            </h1>

            <p class="text-lg lg:text-xl text-neutral-500 mb-10 leading-relaxed max-w-lg">
              A marketplace of AI-crafted, peer-reviewed CBC books built by Kenya's finest educators — for every grade, every subject.
            </p>

            <div class="flex flex-wrap gap-3">
              <a :href="loginUrlCreator" class="btn btn-accent btn-large group no-underline inline-flex items-center gap-2">
                Start for Free
                <svg class="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
              </a>
              <a :href="loginUrlGuardian" class="btn btn-outline btn-large no-underline inline-flex items-center gap-2">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                Explore Books
              </a>
            </div>
          </div>

          <!-- Right: Stacked book card visual -->
          <div class="relative h-[460px] flex items-center justify-center">
            <!-- Back: orange tilt -->
            <div class="absolute w-64 h-[340px] bg-accent-200 rounded-3xl rotate-[10deg] shadow-lg"></div>
            <!-- Mid: teal tilt -->
            <div class="absolute w-64 h-[340px] bg-primary-200 rounded-3xl rotate-[-5deg] shadow-xl"></div>
            <!-- Front: book card -->
            <div class="absolute w-64 h-[340px] bg-white rounded-3xl border border-neutral-200 shadow-2xl flex flex-col overflow-hidden">
              <!-- Cover -->
              <div class="bg-primary-600 px-5 pt-5 pb-4 flex-shrink-0">
                <div class="flex items-center justify-between mb-3">
                  <div class="bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg">Grade 5</div>
                  <div class="flex gap-1">
                    <div class="w-2 h-2 bg-white/35 rounded-full"></div>
                    <div class="w-2 h-2 bg-white/35 rounded-full"></div>
                    <div class="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
                <div class="h-28 flex items-center justify-center">
                  <svg class="w-[72px] h-[72px] text-primary-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                  </svg>
                </div>
              </div>
              <!-- Body -->
              <div class="flex-1 p-5 flex flex-col justify-between">
                <div>
                  <div class="text-[10px] font-bold text-primary-600 uppercase tracking-widest mb-1">Science</div>
                  <div class="font-display font-bold text-neutral-900 text-lg leading-tight mb-3">Digital Storytelling</div>
                  <div class="space-y-1.5">
                    <div class="h-1.5 bg-neutral-100 rounded-full"></div>
                    <div class="h-1.5 bg-neutral-100 rounded-full w-5/6"></div>
                    <div class="h-1.5 bg-neutral-100 rounded-full w-4/5"></div>
                  </div>
                </div>
                <div class="flex items-center justify-between mt-3">
                  <div class="flex items-center gap-1">
                    <div class="w-5 h-5 bg-primary-600 rounded-md flex items-center justify-center">
                      <svg class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    </div>
                    <div class="w-5 h-5 bg-primary-600 rounded-md flex items-center justify-center">
                      <svg class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    </div>
                    <div class="w-5 h-5 bg-neutral-100 rounded-md"></div>
                  </div>
                  <span class="font-display font-bold text-accent-600 text-sm">KSh 150</span>
                </div>
              </div>
            </div>
            <!-- Floating "Reviewed" badge -->
            <div class="absolute top-10 -right-4 bg-accent-500 text-white text-[11px] font-bold px-3 py-2 rounded-xl shadow-lg rotate-3 flex items-center gap-1.5 pointer-events-none">
              <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Reviewed
            </div>
            <!-- Floating "AI" badge -->
            <div class="absolute bottom-16 -left-6 bg-white border border-neutral-200 text-neutral-800 text-[11px] font-semibold px-3 py-2 rounded-xl shadow-lg -rotate-2 flex items-center gap-1.5 pointer-events-none">
              <svg class="w-3.5 h-3.5 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
              AI Generated
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== SUBJECTS TICKER ========== -->
    <div class="bg-primary-600 border-y-4 border-primary-700 py-3.5 overflow-hidden">
      <div class="ticker-track flex items-center gap-0 whitespace-nowrap">
        <!-- Group 1 -->
        <div class="ticker-group flex items-center gap-10 flex-shrink-0 px-10">
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Mathematics
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Science
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            English
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Kiswahili
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Social Studies
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Creative Arts
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Agriculture
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Health Education
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
        </div>
        <!-- Group 2 — identical duplicate for seamless loop -->
        <div class="ticker-group flex items-center gap-10 flex-shrink-0 px-10" aria-hidden="true">
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Mathematics
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Science
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            English
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Kiswahili
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Social Studies
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Creative Arts
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Agriculture
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
          <div class="flex items-center gap-2.5 text-white font-semibold text-sm">
            <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            Health Education
          </div>
          <div class="w-1 h-1 bg-primary-400 rounded-full flex-shrink-0"></div>
        </div>
      </div>
    </div>

    <!-- ========== STATS ========== -->
    <section class="bg-neutral-950 py-14">
      <div class="container">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div class="text-center px-4 py-2">
            <div class="font-display font-bold text-accent-400 stat-number mb-1">500+</div>
            <div class="text-neutral-500 text-sm">Books Published</div>
          </div>
          <div class="text-center px-4 py-2">
            <div class="font-display font-bold text-accent-400 stat-number mb-1">2,400+</div>
            <div class="text-neutral-500 text-sm">Active Learners</div>
          </div>
          <div class="text-center px-4 py-2">
            <div class="font-display font-bold text-accent-400 stat-number mb-1">70%</div>
            <div class="text-neutral-500 text-sm">Revenue to Creators</div>
          </div>
          <div class="text-center px-4 py-2">
            <div class="font-display font-bold text-accent-400 stat-number mb-1">100%</div>
            <div class="text-neutral-500 text-sm">CBC Aligned</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== HOW IT WORKS ========== -->
    <section id="how-it-works" class="py-24 bg-white">
      <div class="container">
        <div class="text-center mb-16">
          <div class="inline-flex items-center gap-2 bg-primary-100 text-primary-700 text-sm font-semibold px-4 py-2 rounded-full mb-5">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2"/>
            </svg>
            The Process
          </div>
          <h2 class="font-display font-bold text-neutral-900 text-4xl lg:text-5xl mb-4">How it all works</h2>
          <p class="text-neutral-500 text-lg max-w-xl mx-auto">A four-step cycle that turns teaching expertise into learning gold.</p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">

          <!-- Step 1 -->
          <div class="relative bg-white border-2 border-neutral-100 rounded-2xl p-6 hover:border-primary-200 hover:shadow-lg transition-all duration-300 group">
            <div class="absolute -top-4 -right-4 w-11 h-11 bg-primary-600 text-white font-display font-bold text-base rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200">01</div>
            <div class="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
            </div>
            <h3 class="font-display font-bold text-neutral-900 text-xl mb-2">Create</h3>
            <p class="text-neutral-500 text-sm leading-relaxed">Expert teachers use our AI wizard to craft interactive CBC books and STEM materials.</p>
          </div>

          <!-- Step 2 — offset on desktop -->
          <div class="relative bg-white border-2 border-neutral-100 rounded-2xl p-6 hover:border-primary-200 hover:shadow-lg transition-all duration-300 group lg:mt-10">
            <div class="absolute -top-4 -right-4 w-11 h-11 bg-primary-600 text-white font-display font-bold text-base rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200">02</div>
            <div class="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            <h3 class="font-display font-bold text-neutral-900 text-xl mb-2">Review</h3>
            <p class="text-neutral-500 text-sm leading-relaxed">Certified peers audit every submission for CBC accuracy, safety and curriculum alignment.</p>
          </div>

          <!-- Step 3 -->
          <div class="relative bg-white border-2 border-neutral-100 rounded-2xl p-6 hover:border-accent-200 hover:shadow-lg transition-all duration-300 group">
            <div class="absolute -top-4 -right-4 w-11 h-11 bg-accent-500 text-white font-display font-bold text-base rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200">03</div>
            <div class="w-12 h-12 bg-accent-50 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-accent-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
              </svg>
            </div>
            <h3 class="font-display font-bold text-neutral-900 text-xl mb-2">Buy</h3>
            <p class="text-neutral-500 text-sm leading-relaxed">Guardians browse and purchase verified content for their child's specific grade and subjects.</p>
          </div>

          <!-- Step 4 — offset on desktop -->
          <div class="relative bg-white border-2 border-neutral-100 rounded-2xl p-6 hover:border-accent-200 hover:shadow-lg transition-all duration-300 group lg:mt-10">
            <div class="absolute -top-4 -right-4 w-11 h-11 bg-accent-500 text-white font-display font-bold text-base rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-200">04</div>
            <div class="w-12 h-12 bg-accent-50 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-accent-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
              </svg>
            </div>
            <h3 class="font-display font-bold text-neutral-900 text-xl mb-2">Learn</h3>
            <p class="text-neutral-500 text-sm leading-relaxed">Children open their digital backpack and dive into engaging stories and interactive exercises.</p>
          </div>

        </div>
      </div>
    </section>

    <!-- ========== FEATURES (Bento Grid) ========== -->
    <section id="features" class="py-24 bg-neutral-50">
      <div class="container">
        <div class="text-center mb-16">
          <div class="inline-flex items-center gap-2 bg-accent-100 text-accent-700 text-sm font-semibold px-4 py-2 rounded-full mb-5">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
            </svg>
            Built Different
          </div>
          <h2 class="font-display font-bold text-neutral-900 text-4xl lg:text-5xl mb-4">Tools built for education</h2>
          <p class="text-neutral-500 text-lg max-w-xl mx-auto">Everything teachers and families need — no extra complexity, no fluff.</p>
        </div>

        <!-- Bento grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

          <!-- Large: AI Creation (spans 2 cols on lg) -->
          <div class="lg:col-span-2 bg-primary-600 rounded-3xl p-8 text-white flex flex-col md:flex-row gap-8 items-center overflow-hidden relative">
            <!-- Solid circle decoration (no gradient) -->
            <div class="absolute top-0 right-0 w-56 h-56 bg-primary-500 rounded-full translate-x-1/3 -translate-y-1/3 pointer-events-none"></div>
            <div class="relative z-10 flex-1">
              <div class="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-5">
                <svg class="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                </svg>
              </div>
              <h3 class="font-display font-bold text-2xl mb-3">AI Content Wizard</h3>
              <p class="text-primary-100 leading-relaxed mb-6">Generate localized CBC stories, STEM diagrams, and math worksheets in minutes. Our AI speaks Kenyan curriculum.</p>
              <ul class="space-y-2 mb-6">
                <li class="flex items-center gap-2 text-sm text-primary-100">
                  <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
                  Localized storytelling &amp; illustrations
                </li>
                <li class="flex items-center gap-2 text-sm text-primary-100">
                  <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
                  STEM diagram generation
                </li>
                <li class="flex items-center gap-2 text-sm text-primary-100">
                  <svg class="w-4 h-4 text-accent-300 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
                  Automated math worksheets
                </li>
              </ul>
              <button @click="goToLoginCreator" class="bg-white text-primary-700 font-semibold px-5 py-2.5 rounded-xl text-sm hover:bg-primary-50 transition-colors inline-flex items-center gap-2">
                Try It Free
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
              </button>
            </div>
            <!-- Mini chat mockup -->
            <div class="relative z-10 bg-white/10 border border-white/20 rounded-2xl p-5 w-full md:w-60 flex-shrink-0">
              <div class="flex items-center gap-2 mb-4 pb-3 border-b border-white/15">
                <div class="w-2 h-2 rounded-full bg-accent-400"></div>
                <div class="text-xs text-primary-200 font-medium">AI Creator</div>
              </div>
              <div class="space-y-2.5">
                <div class="bg-white/10 rounded-xl p-2.5">
                  <div class="h-1.5 bg-white/30 rounded w-full mb-1.5"></div>
                  <div class="h-1.5 bg-white/30 rounded w-3/4"></div>
                </div>
                <div class="bg-accent-500 rounded-xl p-2.5 ml-4">
                  <div class="h-1.5 bg-white/50 rounded w-5/6 mb-1.5"></div>
                  <div class="h-1.5 bg-white/50 rounded w-2/3"></div>
                </div>
                <div class="bg-white/10 rounded-xl p-2.5">
                  <div class="h-1.5 bg-white/30 rounded w-4/5"></div>
                </div>
              </div>
              <div class="mt-3 flex gap-2">
                <div class="flex-1 bg-white/10 rounded-lg h-8 border border-white/20"></div>
                <div class="w-8 h-8 bg-accent-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Peer Review -->
          <div class="bg-white border-2 border-neutral-100 rounded-3xl p-7 hover:border-primary-200 hover:shadow-lg transition-all duration-300">
            <div class="w-12 h-12 bg-success-100 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-success-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
            <h3 class="font-display font-bold text-neutral-900 text-xl mb-2">Quality Gate</h3>
            <p class="text-neutral-500 text-sm leading-relaxed">Every book passes multi-tier peer review before reaching any child. No shortcuts. No compromises.</p>
          </div>

          <!-- Secure Payments -->
          <div class="bg-accent-500 rounded-3xl p-7 text-white">
            <div class="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
            </div>
            <h3 class="font-display font-bold text-xl mb-2">Secure Payments</h3>
            <p class="text-white/80 text-sm leading-relaxed">M-Pesa and card payments via Idyangu. Creators earn 70% of every sale, automatically.</p>
          </div>

          <!-- Digital Backpack -->
          <div class="bg-neutral-900 rounded-3xl p-7 text-white">
            <div class="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
            </div>
            <h3 class="font-display font-bold text-xl mb-2">Digital Backpack</h3>
            <p class="text-neutral-400 text-sm leading-relaxed">Kids access their library from any device. Offline-ready, distraction-free, built for little learners.</p>
          </div>

          <!-- All CBC Grades -->
          <div class="bg-white border-2 border-neutral-100 rounded-3xl p-7 hover:border-accent-200 hover:shadow-lg transition-all duration-300">
            <div class="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center mb-5">
              <svg class="w-6 h-6 text-accent-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
              </svg>
            </div>
            <h3 class="font-display font-bold text-neutral-900 text-xl mb-2">All CBC Grades</h3>
            <p class="text-neutral-500 text-sm leading-relaxed">From Grade 1 through JSS, every subject and level is covered by specialist educators.</p>
          </div>

        </div>
      </div>
    </section>

    <!-- ========== FOR YOU (Role cards) ========== -->
    <section id="for-you" class="py-24 bg-white">
      <div class="container">
        <div class="text-center mb-16">
          <h2 class="font-display font-bold text-neutral-900 text-4xl lg:text-5xl mb-4">Who is Tunga for?</h2>
          <p class="text-neutral-500 text-lg max-w-xl mx-auto">Everyone who believes in Kenya's next generation has a place here.</p>
        </div>

        <div class="grid md:grid-cols-3 gap-6">

          <!-- Creators -->
          <div class="bg-primary-600 rounded-3xl p-8 text-white relative overflow-hidden">
            <div class="absolute bottom-0 right-0 w-44 h-44 bg-primary-500 rounded-full translate-x-1/3 translate-y-1/3 pointer-events-none"></div>
            <div class="relative z-10">
              <div class="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
              </div>
              <h3 class="font-display font-bold text-2xl mb-3">For Creators</h3>
              <p class="text-primary-100 leading-relaxed mb-6">Turn your teaching expertise into a passive income stream. Earn 70% royalties on every sale with AI-assisted creation tools.</p>
              <button @click="goToLoginCreator" class="bg-white text-primary-700 font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-primary-50 transition-colors inline-flex items-center gap-2">
                Start Creating
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
              </button>
            </div>
          </div>

          <!-- Guardians -->
          <div class="bg-neutral-900 rounded-3xl p-8 text-white relative overflow-hidden">
            <div class="absolute bottom-0 right-0 w-44 h-44 bg-neutral-800 rounded-full translate-x-1/3 translate-y-1/3 pointer-events-none"></div>
            <div class="relative z-10">
              <div class="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              </div>
              <h3 class="font-display font-bold text-2xl mb-3">For Guardians</h3>
              <p class="text-neutral-400 leading-relaxed mb-6">Browse verified CBC materials by grade and subject. Buy only what your child needs, then track their progress in one place.</p>
              <button @click="goToLoginGuardian" class="bg-white text-neutral-900 font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-neutral-100 transition-colors inline-flex items-center gap-2">
                Browse Books
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
              </button>
            </div>
          </div>

          <!-- Learners -->
          <div class="bg-accent-500 rounded-3xl p-8 text-white relative overflow-hidden">
            <div class="absolute bottom-0 right-0 w-44 h-44 bg-accent-400 rounded-full translate-x-1/3 translate-y-1/3 pointer-events-none"></div>
            <div class="relative z-10">
              <div class="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                <svg class="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                </svg>
              </div>
              <h3 class="font-display font-bold text-2xl mb-3">For Learners</h3>
              <p class="text-accent-100 leading-relaxed mb-6">Open your digital backpack and dive into fun stories, colorful diagrams, and interactive exercises made just for you.</p>
              <button @click="goToLoginGuardian" class="bg-white text-accent-700 font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-accent-50 transition-colors inline-flex items-center gap-2">
                My Backpack
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
              </button>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- ========== FINAL CTA ========== -->
    <section class="py-20 bg-primary-600">
      <div class="container text-center">
        <div class="max-w-3xl mx-auto">
          <div class="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <svg class="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
          </div>
          <h2 class="font-display font-bold text-white text-4xl lg:text-5xl mb-6">Ready to get started?</h2>
          <p class="text-primary-100 text-xl mb-10 leading-relaxed">Join thousands of Kenya's teachers and guardians already transforming how the next generation learns.</p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <a :href="loginUrlCreator" class="bg-accent-500 text-white font-bold text-base px-8 py-4 rounded-xl hover:bg-accent-600 transition-colors inline-flex items-center justify-center gap-2 shadow-lg no-underline">
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
              Create Your Account
            </a>
            <a :href="loginUrlGuardian" class="bg-white/10 text-white font-bold text-base px-8 py-4 rounded-xl hover:bg-white/20 transition-colors border border-white/30 inline-flex items-center justify-center gap-2 no-underline">
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              Browse First
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== FOOTER ========== -->
    <footer class="bg-neutral-950 text-neutral-400 py-12">
      <div class="container">
        <div class="grid md:grid-cols-4 gap-8 mb-10">

          <div class="md:col-span-2">
            <div class="flex items-center gap-2.5 mb-4">
              <div class="w-9 h-9 bg-primary-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
                </svg>
              </div>
              <span class="font-display font-bold text-white text-lg">Tunga</span>
            </div>
            <p class="text-sm leading-relaxed max-w-xs">The premier CBC educational marketplace for Kenya. Built to empower teachers, guardians, and learners.</p>
          </div>

          <div>
            <h4 class="text-white font-semibold text-sm mb-4">Platform</h4>
            <ul class="space-y-2.5 text-sm">
              <li><a href="#" class="hover:text-white transition-colors no-underline">Marketplace</a></li>
              <li><a href="#" class="hover:text-white transition-colors no-underline">Creator Studio</a></li>
              <li><a href="#" class="hover:text-white transition-colors no-underline">Peer Review</a></li>
              <li><a href="#" class="hover:text-white transition-colors no-underline">Learning Hub</a></li>
            </ul>
          </div>

          <div>
            <h4 class="text-white font-semibold text-sm mb-4">Company</h4>
            <ul class="space-y-2.5 text-sm">
              <li><a href="#" class="hover:text-white transition-colors no-underline">About Us</a></li>
              <li><a href="#" class="hover:text-white transition-colors no-underline">Privacy Policy</a></li>
              <li><a href="#" class="hover:text-white transition-colors no-underline">Terms of Use</a></li>
              <li><a href="#" class="hover:text-white transition-colors no-underline">Contact</a></li>
            </ul>
          </div>

        </div>

        <div class="pt-8 border-t border-neutral-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-neutral-600">
          <span>&copy; 2026 Tunga. All rights reserved.</span>
          <div class="flex items-center gap-2">
            <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="5" width="20" height="14" rx="2"/>
              <path d="M2 10h20"/>
            </svg>
            <span>Secured by Idyangu</span>
          </div>
        </div>

      </div>
    </footer>

  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const authStore = useAuthStore()

// Direct links to API – backend responds with 302 to IdYangu (same pattern as schoolbus)
const loginUrlCreator = '/api/auth/idyangu/login?redirect=' + encodeURIComponent('/creator')
const loginUrlGuardian = '/api/auth/idyangu/login?redirect=' + encodeURIComponent('/guardian')

function goToLoginCreator() {
  window.location.href = loginUrlCreator
}

function goToLoginGuardian() {
  window.location.href = loginUrlGuardian
}

onMounted(async () => {
  const hasSession = await authStore.checkSession()
  if (hasSession && authStore.isAuthenticated && authStore.user) {
    if (authStore.user.is_creator) {
      router.push('/creator')
    } else {
      router.push('/guardian')
    }
  }
})
</script>

<style scoped>
/* Responsive hero headline */
.hero-headline {
  font-size: clamp(2.5rem, 6vw, 5rem);
}

/* Stats large numbers */
.stat-number {
  font-size: clamp(2.25rem, 4vw, 3rem);
  line-height: 1;
}

/* Subjects ticker animation */
.ticker-track {
  animation: ticker-scroll 32s linear infinite;
  will-change: transform;
}

@keyframes ticker-scroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

/* Suppress underlines on nav links (override global `a` style) */
.no-underline {
  text-decoration: none;
}
</style>
